import { useMemo } from "react";
import DOMPurify from "dompurify";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

export function useRichBody(html) {
  return useMemo(() => {
    if (!html || typeof DOMParser === "undefined") return { html: html || "", toc: [] };
    const doc = new DOMParser().parseFromString(DOMPurify.sanitize(html), "text/html");
    doc.querySelectorAll("script,style,iframe").forEach((n) => n.remove());
    const toc = [];
    doc.querySelectorAll("h2,h3").forEach((h) => {
      const text = h.textContent.trim();
      if (!text) return;
      h.id = h.id || slug(text);
      toc.push({ id: h.id, text, level: h.tagName === "H2" ? 2 : 3 });
    });
    doc.querySelectorAll("a[href]").forEach((a) => {
      const href = a.getAttribute("href");
      if (/^https?:\/\//i.test(href) && !/^https?:\/\/(www\.)?acponline\.co\.nz/i.test(href)) {
        a.setAttribute("target", "_blank");
        const rel = new Set((a.getAttribute("rel") || "").split(" ").filter(Boolean));
        rel.add("noopener"); rel.add("noreferrer");
        a.setAttribute("rel", [...rel].join(" "));
      }
    });
    return { html: doc.body.innerHTML, toc };
  }, [html]);
}

export default function RichBody({ html, testid }) {
  const { html: out } = useRichBody(html);
  return <div className="cms-body prose-sm max-w-none" data-testid={testid} dangerouslySetInnerHTML={{ __html: out }} />;
}

export function Toc({ toc }) {
  if (toc.length < 3) return null;
  return (
    <nav className="rounded-xl border bg-white p-4 text-sm" data-testid="article-toc">
      <div className="font-bold text-steel mb-2">On this page</div>
      <ul className="space-y-1">
        {toc.map((t) => <li key={t.id} className={t.level === 3 ? "pl-3" : ""}><a href={`#${t.id}`} className="text-slate-600 hover:text-accent">{t.text}</a></li>)}
      </ul>
    </nav>
  );
}

export function FaqList({ faq }) {
  const items = (faq || []).filter((f) => f.q && f.a);
  if (!items.length) return null;
  return (
    <section className="space-y-2" data-testid="faq-list">
      <h2 className="font-heading font-bold text-xl text-steel">Frequently asked questions</h2>
      {items.map((f, i) => (
        <details key={i} className="rounded-lg border bg-white p-3">
          <summary className="font-semibold cursor-pointer text-steel">{f.q}</summary>
          <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{f.a}</p>
        </details>))}
    </section>
  );
}
